import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Icon } from '@/shared/ui/Icon';
import { useToast } from '@/shared/ui/Toast';
import {
  deleteMyPayoutAccount,
  getMyPayoutAccount,
  updateMyPayoutAccount,
  type PayoutAccount,
} from '@/shared/api/payoutAccount';

const EMPTY_PAYOUT_ACCOUNT: PayoutAccount = {
  bankName: '',
  accountNumber: '',
  holderName: '',
};

function normalizePayoutAccount(account: Partial<PayoutAccount> | null | undefined): PayoutAccount | null {
  if (!account) return null;
  const normalized = {
    bankName: account.bankName ?? '',
    accountNumber: account.accountNumber ?? '',
    holderName: account.holderName ?? '',
  };
  return normalized.bankName.trim() || normalized.accountNumber.trim() || normalized.holderName.trim()
    ? normalized
    : null;
}

// 정산 수령 계좌 — 앱은 자금을 중개하지 않고, 같은 모임 멤버에게
// "계좌·금액 복사"용으로만 노출된다.
export function PayoutAccountCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<PayoutAccount>(EMPTY_PAYOUT_ACCOUNT);

  const accountQ = useQuery({ queryKey: ['my-payout-account'], queryFn: getMyPayoutAccount });
  const account = normalizePayoutAccount(accountQ.data);

  useEffect(() => {
    if (account) setForm(account);
  }, [account]);

  const saveMut = useMutation({
    mutationFn: () => updateMyPayoutAccount(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-payout-account'] });
      setEditing(false);
      toast.success('정산 계좌를 저장했어요.');
    },
    onError: () => toast.error('계좌 저장에 실패했어요.'),
  });
  const deleteMut = useMutation({
    mutationFn: deleteMyPayoutAccount,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-payout-account'] });
      setForm(EMPTY_PAYOUT_ACCOUNT);
      setEditing(false);
      toast.show('정산 계좌를 삭제했어요.');
    },
    onError: () => toast.error('계좌 삭제에 실패했어요.'),
  });

  const canSave =
    form.bankName.trim().length > 0 &&
    form.accountNumber.trim().length > 0 &&
    form.holderName.trim().length > 0;

  if (editing) {
    return (
      <section className="rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-3.5" aria-label="정산 계좌 등록 또는 수정">
        <div className="mb-3 text-center">
          <span className="mx-auto flex h-[54px] w-[54px] items-center justify-center rounded-[18px] bg-primary-soft text-primary">
            <Icon name="wallet" size={24} />
          </span>
          <h3 className="mt-3 text-[18px] font-semibold tracking-[-0.5px] text-ink">받을 계좌를 등록해요</h3>
          <p className="mt-1 text-[12px] leading-relaxed text-ink-muted">정산에서 친구들이 바로 계좌를 복사해 송금할 수 있어요.</p>
        </div>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) saveMut.mutate();
          }}
        >
          <Input
            label="은행"
            value={form.bankName}
            onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))}
            placeholder="예: 카카오뱅크"
            maxLength={40}
            required
          />
          <Input
            label="계좌번호"
            value={form.accountNumber}
            onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))}
            placeholder="숫자와 - 만 입력"
            inputMode="numeric"
            maxLength={40}
            required
          />
          <Input
            label="예금주"
            value={form.holderName}
            onChange={(e) => setForm((f) => ({ ...f, holderName: e.target.value }))}
            placeholder="예금주 이름"
            maxLength={40}
            required
          />
          <div className="flex gap-2">
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={!canSave || saveMut.isPending}
            >
              {saveMut.isPending ? '저장 중…' : '저장'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setEditing(false);
                setForm(account ?? EMPTY_PAYOUT_ACCOUNT);
              }}
            >
              취소
            </Button>
            {account && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => deleteMut.mutate()}
                disabled={deleteMut.isPending}
              >
                삭제
              </Button>
            )}
          </div>
          <p className="text-center text-[11px] leading-relaxed text-ink-faint">계좌 정보는 정산 참여자에게만 보여요.</p>
        </form>
      </section>
    );
  }

  return (
    <section className="rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-3.5" aria-label="정산 계좌">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-[13px] font-semibold text-ink">정산 계좌</h3>
        {account ? (
          <span className="rounded-full bg-success-soft px-2 py-1 text-[10px] font-semibold text-success-strong">
            등록됨
          </span>
        ) : null}
      </div>

      {account ? (
        <>
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-[13px] font-semibold text-primary">
              {account.bankName.trim().charAt(0) || '은'}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-[14px] font-semibold text-ink">{account.bankName}</span>
              <span className="mt-0.5 block truncate text-[11px] text-ink-muted">
                {account.accountNumber} · {account.holderName}
              </span>
            </span>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="flex-1 rounded-[11px] bg-primary px-2 py-2.5 text-[12px] font-semibold text-white focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              계좌 수정
            </button>
            <button
              type="button"
              onClick={() => deleteMut.mutate()}
              disabled={deleteMut.isPending}
              className="flex-1 rounded-[11px] bg-surface-muted px-2 py-2.5 text-[12px] font-semibold text-ink-muted disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              {deleteMut.isPending ? '삭제 중…' : '삭제'}
            </button>
          </div>
        </>
      ) : (
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
            <Icon name="wallet" size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold text-ink">받을 계좌를 등록해요</p>
            <p className="mt-0.5 text-[11px] text-ink-muted">송금할 때 계좌·금액 복사에 사용돼요.</p>
          </div>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="shrink-0 rounded-[11px] bg-primary px-3 py-2.5 text-[12px] font-semibold text-white focus-visible:outline-none focus-visible:shadow-focus-ring"
          >
            등록
          </button>
        </div>
      )}
    </section>
  );
}
